import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Wand2, Sparkles, Loader2, Type, Palette, MoveVertical, Play, X, Smile, Bookmark, Trash2, Plus, Highlighter, Layers, Film, Upload, CheckCircle2, AlertTriangle, RefreshCw, Eye, Star } from "lucide-react";
import { api } from "@/lib/api";
import { drawFrame, totalDuration } from "@/lib/animator";
import { baseStyle, applyGlobalStyle, STYLE_FIELDS } from "@/lib/style";
import { useCustomFonts, uploadFont, ensureScriptFonts, ensureTextFonts, collectCaptionText } from "@/lib/fonts";
import FontPicker from "@/components/FontPicker";
import StyleEditor from "@/components/StyleEditor";
import BrandThumbnail from "@/components/BrandThumbnail";
import SavedDesignsDialog from "@/components/SavedDesignsDialog";
import { CaptionFxControl } from "@/components/CaptionFxControl";
import { presetsByCategory, applyPreset as applyTextPreset, previewStyleFor, TEXT_ANIMATION_PRESETS } from "@/lib/textAnimationPresets";
import PresetPreview from "@/components/PresetPreview";

const PRESET_GROUPS = presetsByCategory();
const ALL_PRESETS = TEXT_ANIMATION_PRESETS;

const PRESETS = [
  { id: "bouncy", name: "Bouncy", d: "Scale up with overshoot", target: "word" },
  { id: "pop", name: "Pop", d: "Quick scale-in", target: "word" },
  { id: "fade", name: "Fade", d: "Fade in / out", target: "word" },
  { id: "slide-up", name: "Slide Up", d: "Rise into place", target: "word" },
  { id: "slide-down", name: "Slide Down", d: "Drop into place", target: "word" },
  { id: "slide-left", name: "Slide Left", d: "Enter from left", target: "word" },
  { id: "slide-right", name: "Slide Right", d: "Enter from right", target: "word" },
  { id: "typewriter", name: "Typewriter", d: "Reveal characters", target: "word" },
  { id: "kinetic", name: "Kinetic", d: "Dynamic motion", target: "word" },
  { id: "punch", name: "Punch", d: "Strong scale + rotate", target: "word" },
  { id: "minimal", name: "Minimal", d: "Clean fade + scale", target: "word" },
  { id: "karaoke", name: "Karaoke", d: "Highlight as spoken", target: "karaoke" },
  { id: "motion-blur", name: "Motion Blur", d: "Directional blur entry", target: "word" },
];

const FONTS = ["Inter", "Montserrat", "Poppins", "Roboto", "Anton", "Bebas Neue", "Oswald", "Arial", "Helvetica", "Georgia", "Times New Roman"];
const WEIGHTS = [{ l: "Light", v: 300 }, { l: "Regular", v: 400 }, { l: "Medium", v: 500 }, { l: "Semi Bold", v: 600 }, { l: "Bold", v: 700 }, { l: "Extra Bold", v: 800 }, { l: "Black", v: 900 }];
const POSITIONS = [{ l: "Top", v: "top" }, { l: "Upper Middle", v: "upper-middle" }, { l: "Centre", v: "center" }, { l: "Lower Middle", v: "lower-middle" }, { l: "Bottom", v: "bottom" }];
const TARGETS = [{ l: "Word by word", v: "word" }, { l: "Sentence", v: "sentence" }, { l: "Phrase", v: "phrase" }, { l: "Karaoke", v: "karaoke" }];
const COLOR_PRESETS = [
  { l: "White + Yellow", t: "#FFFFFF", h: "#FFD400" },
  { l: "White + Red", t: "#FFFFFF", h: "#FF3B30" },
  { l: "White + Blue", t: "#FFFFFF", h: "#00A3FF" },
  { l: "White + Green", t: "#FFFFFF", h: "#34C759" },
  { l: "Black + White", t: "#111111", h: "#FFFFFF" },
];

const SAMPLE_PROMPT = `Create energetic Instagram Reel captions.
Use a bold modern font.
Make each spoken word pop onto the screen individually with a slight bounce.
Highlight important words in yellow, white for normal words.
Place the text in the lower middle. Add a subtle black outline.
Keep the animation fast and energetic.`;

const EMOJI_PALETTE = ["🔥", "✨", "🚀", "❤️", "💯", "🎉", "👑", "⭐", "🙌", "😍", "👀", "💥"];

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-5">
      <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]"><Icon size={13} /> {title}</div>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="mb-1.5 text-[11px] text-[#A1A1AA]">{label}</div>
      <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0" />
        <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent font-plex text-xs uppercase outline-none" />
      </div>
    </div>
  );
}

export default function AnimationModal({ open, onOpenChange, config, onApply, segments, projectId, onApplySavedDesign = null }) {
  const [savedOpen, setSavedOpen] = useState(false);
  const [cfg, setCfg] = useState(config || {});
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tap_fav_presets") || "[]"); } catch { return []; }
  });
  const toggleFav = (id, e) => {
    e && e.stopPropagation();
    setFavs((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem("tap_fav_presets", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const renderPresetCard = (p) => {
    const selected = ((cfg || {}).animationPresetId || ((cfg || {}).animation === "none" ? "none" : "")) === p.id;
    const isFav = favs.includes(p.id);
    return (
      <div key={p.id} className={`relative rounded-lg border p-2.5 transition-colors ${selected ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
        <button data-testid={`fav-${p.id}`} onClick={(e) => toggleFav(p.id, e)} title={isFav ? "Unfavourite" : "Favourite"}
          className="absolute right-1 top-1 z-10 rounded p-0.5 hover:bg-white/10">
          <Star size={13} className={isFav ? "text-[#FFD400]" : "text-[#71717a]"} fill={isFav ? "#FFD400" : "none"} />
        </button>
        <button data-testid={`anim-preset-${p.id}`} onClick={() => setCfg((c) => applyTextPreset(c, p))} className="block w-full text-left">
          {p.popular && <span className="absolute left-1.5 top-1.5 rounded-full bg-[#8B5CF6] px-1.5 py-0.5 text-[8px] font-bold text-white">Popular</span>}
          <div className="mb-1.5 flex h-8 items-center justify-center overflow-hidden text-[15px]"><PresetPreview preset={p} /></div>
          <div className="text-xs font-bold">{p.name}</div>
          <div className="text-[10px] leading-tight text-[#A1A1AA]">{p.desc}</div>
        </button>
      </div>
    );
  };
  const customFonts = useCustomFonts();
  const fontFileRef = useRef(null);
  const [fontBusy, setFontBusy] = useState(false);
  const onUploadFont = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFontBusy(true);
    try {
      const doc = await uploadFont(f);
      setCfg((c) => ({ ...c, fontFamily: doc.family }));
      if (doc.glyph_warning) toast.warning(doc.glyph_warning);
      else toast.success(`Font "${doc.family}" added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload that font.");
    }
    setFontBusy(false);
  };
  const [prompt, setPrompt] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [presets, setPresets] = useState([]);
  const [brandName, setBrandName] = useState("");
  const [saveName, setSaveName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [emoji, setEmoji] = useState("🔥");
  const [emojiWord, setEmojiWord] = useState("");
  const [highlighting, setHighlighting] = useState(false);
  const [hiWord, setHiWord] = useState("");
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  // --- Reference Animation Import (v1) ---
  const refFileRef = useRef(null);
  const [refFile, setRefFile] = useState(null);
  const [refUrl, setRefUrl] = useState(null);
  const [refBusy, setRefBusy] = useState(false);
  const [refStep, setRefStep] = useState(0);
  const [refResult, setRefResult] = useState(null);
  const [refElement, setRefElement] = useState(0);
  const [compare, setCompare] = useState(false);
  const [refOpacity, setRefOpacity] = useState(50);
  const REF_STEPS = ["Uploading reference", "Analyzing frames", "Detecting text", "Analyzing motion", "Building animation"];

  const pickRef = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (refUrl) URL.revokeObjectURL(refUrl);
    setRefFile(f); setRefUrl(URL.createObjectURL(f)); setRefResult(null);
  };
  const analyzeRef = async () => {
    if (!refFile || refBusy) return;
    setRefBusy(true); setRefResult(null); setRefStep(0);
    const tick = setInterval(() => setRefStep((s) => Math.min(s + 1, REF_STEPS.length - 1)), 2200);
    try {
      const res = await api.analyzeReference(refFile);
      clearInterval(tick); setRefStep(REF_STEPS.length - 1);
      setRefResult(res); setRefElement(0);
      // Merge the dominant detected element's preset into the editable config.
      const el0 = (res.elements && res.elements[0]) || { preset: res.preset };
      setCfg((c) => ({ ...c, ...el0.preset.config }));
      toast.success("Reference animation detected — recreated as an editable preset");
    } catch (e) {
      clearInterval(tick);
      toast.error(e?.response?.data?.detail || "Couldn't analyze that reference video.");
    }
    setRefBusy(false);
  };
  useEffect(() => () => { if (refUrl) URL.revokeObjectURL(refUrl); }, [refUrl]);


  const flatWords = useMemo(() => (segments || []).flatMap((s) => s.words || []), [segments]);

  useEffect(() => { if (open) { setCfg(config || {}); api.listBrandPresets().then(setPresets).catch(() => {}); } }, [open, config]);
  useEffect(() => { if (open) setSaveName(`My Style · ${new Date().toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`); }, [open]);
  // Save the current look to My Text Presets (newest saves are listed first in the presets library).
  const saveAsTextPreset = async () => {
    const name = saveName.trim();
    if (!name) { toast.error("Name your preset"); return; }
    const b = baseStyle(cfg);
    const style = {};
    STYLE_FIELDS.forEach((k) => { if (k !== "animation" && b[k] !== undefined && b[k] !== null) style[k] = b[k]; });
    const definition = { style, animation: cfg.animation || "pop", ...(cfg.fx ? { fx: cfg.fx } : {}) };
    if (cfg.fontMode && cfg.fontMode !== "single" && cfg.fontFamilyB) Object.assign(definition, { fontMode: cfg.fontMode, fontFamilyB: cfg.fontFamilyB, fontWeightB: cfg.fontWeightB, fontStyleB: cfg.fontStyleB });
    setSavingPreset(true);
    try { await api.createPreset({ name, category: "caption", definition }); window.dispatchEvent(new Event("cm-presets-changed")); toast.success(`Saved "${name}" to My Text Presets`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save preset"); }
    setSavingPreset(false);
  };

  // Legacy control keys → their canonical counterpart. baseStyle/_base_style read the canonical
  // key FIRST, so a legacy-only write (e.g. the Colours "Outline" switch) would be shadowed by a
  // stale canonical value left behind by a preset. Keep both in sync on every set.
  const LEGACY_TO_CANON = { outline: "strokeEnabled", outlineColor: "strokeColor", outlineWidth: "strokeWidth", shadow: "shadowEnabled", threeD: "threeDEnabled", glow: "glowEnabled", background: "bgEnabled" };
  const set = (k, v) => setCfg((c) => {
    const n = { ...c, [k]: v };
    const canon = LEGACY_TO_CANON[k];
    if (canon) n[canon] = v;
    return n;
  });

  const saveBrand = async () => {
    if (!brandName.trim()) { toast.error("Name your brand style"); return; }
    const { wordStyles, ...defaults } = cfg;
    try { await api.createBrandPreset(brandName.trim(), defaults); setBrandName(""); setPresets(await api.listBrandPresets()); toast.success("Brand style saved"); }
    catch { toast.error("Could not save brand style"); }
  };
  const applyPreset = (p) => { setCfg((c) => ({ ...c, ...p.config, wordStyles: c.wordStyles || {} })); toast.success(`Applied "${p.name}"`); };
  const delPreset = async (id) => { try { await api.deleteBrandPreset(id); setPresets(await api.listBrandPresets()); } catch {} };

  const uid = () => `${Date.now()}-${Math.round(Math.random() * 1e6)}`;
  const addEmoji = () => {
    if (!emojiWord) { toast.error("Pick a word for the emoji"); return; }
    const w = flatWords.find((x) => x.word_id === emojiWord);
    setCfg((c) => ({ ...c, emojiTriggers: [...(c.emojiTriggers || []).filter((t) => t.word_id !== emojiWord), { id: uid(), word_id: emojiWord, emoji, time: w ? w.start : 0 }] }));
  };
  const removeEmoji = (id) => setCfg((c) => ({ ...c, emojiTriggers: (c.emojiTriggers || []).filter((t) => (t.id || t.word_id) !== id) }));

  const autoHighlight = async () => {
    if (!projectId) { toast.error("Save the project first"); return; }
    setHighlighting(true);
    try { const r = await api.autoHighlight(projectId); setCfg((c) => ({ ...c, highlightWordIds: r.highlightWordIds || [] })); toast.success(`AI highlighted ${(r.highlightWordIds || []).length} words`); }
    catch { toast.error("Auto-highlight failed"); }
    setHighlighting(false);
  };
  const addHi = () => { if (!hiWord) return; setCfg((c) => ({ ...c, highlightWordIds: [...new Set([...(c.highlightWordIds || []), hiWord])] })); };
  const removeHi = (wid) => setCfg((c) => ({ ...c, highlightWordIds: (c.highlightWordIds || []).filter((x) => x !== wid) }));
  const wordText = (wid) => (flatWords.find((w) => w.word_id === wid)?.word) || wid;

  // looping preview
  useEffect(() => {
    if (!open) return;
    ensureScriptFonts();
    ensureTextFonts(collectCaptionText([], segments), [cfg.fontFamily]);
    const dur = totalDuration(segments) || 4;
    let start = performance.now();
    const draw = () => {
      const cv = canvasRef.current;
      if (cv) {
        const t = ((performance.now() - start) / 1000) % dur;
        drawFrame(cv.getContext("2d"), t, cv.width, cv.height, segments, cfg);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [open, cfg, segments]);

  const interpret = async () => {
    if (!prompt.trim()) { toast.error("Describe your animation first"); return; }
    setInterpreting(true);
    try {
      const res = await api.interpret(prompt);
      if (res.config) { setCfg((c) => ({ ...c, ...res.config })); toast.success("AI configured your animation"); }
      else toast.error("Could not interpret the prompt");
    } catch { toast.error("AI interpretation failed"); }
    setInterpreting(false);
  };

  const apply = () => { onOpenChange(false); onApply(cfg); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl gap-0 overflow-hidden border-white/10 bg-[#121212] p-0" data-testid="animation-modal">
        <DialogTitle className="sr-only">Create Text Animation</DialogTitle>
        <div className="grid max-h-[88vh] md:grid-cols-[1fr_340px]">
          {/* Left: controls */}
          <div className="overflow-y-auto p-6">
            <div className="mb-1 flex flex-wrap items-center gap-2 font-display text-xl font-black tracking-tight">
              <Sparkles size={20} className="text-[#8B5CF6]" /> Create Text Animation
              {onApplySavedDesign && (
                <button type="button" onClick={() => setSavedOpen(true)} data-testid="anim-saved-designs-btn"
                  className="ml-auto mr-8 flex items-center gap-1.5 rounded-full border border-[#8B5CF6]/50 bg-[#8B5CF6]/10 px-3 py-1.5 font-sans text-xs font-bold text-[#c4b5fd] transition-colors hover:bg-[#8B5CF6]/20">
                  <Bookmark size={13} /> Saved designs
                </button>
              )}
            </div>
            <p className="mb-5 text-sm text-[#A1A1AA]">Describe the style in plain English, or fine-tune the controls.</p>

            <Section icon={Film} title="Reference animation (import from a video)">
              <input ref={refFileRef} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v" className="hidden" onChange={pickRef} data-testid="reference-file-input" />
              {!refResult && !refBusy && (
                <div className="rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/[0.05] p-3">
                  <div className="flex items-center gap-2">
                    <button type="button" data-testid="reference-upload-btn" onClick={() => refFileRef.current?.click()}
                      className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold text-white hover:border-[#8B5CF6]/60">
                      <Upload size={14} /> {refFile ? "Change reference" : "Upload Reference Video"}
                    </button>
                    {refFile && (
                      <button type="button" data-testid="reference-analyze-btn" onClick={analyzeRef}
                        className="flex items-center gap-2 rounded-lg bg-[#8B5CF6] px-3 py-2 text-xs font-bold text-white hover:bg-[#7c4ddb]">
                        <Sparkles size={14} /> Analyze Animation
                      </button>
                    )}
                  </div>
                  <p className="mt-2 text-[11px] text-[#666]">{refFile ? refFile.name : "Upload a short video (1–10s, MP4/MOV/WebM) containing the text animation you want to recreate."}</p>
                </div>
              )}
              {refBusy && (
                <div className="rounded-xl border border-[#8B5CF6]/40 bg-[#8B5CF6]/[0.07] p-4" data-testid="reference-analyzing">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#c99bff]"><Loader2 size={16} className="animate-spin" /> Analyzing reference animation…</div>
                  <div className="mt-3 space-y-1">
                    {REF_STEPS.map((s, i) => (
                      <div key={s} className={`flex items-center gap-2 text-xs ${i < refStep ? "text-[#00F0FF]" : i === refStep ? "text-white" : "text-[#555]"}`}>
                        {i < refStep ? <CheckCircle2 size={12} /> : i === refStep ? <Loader2 size={12} className="animate-spin" /> : <div className="h-3 w-3 rounded-full border border-current" />} Step {i + 1}/5 {s}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {refResult && !refBusy && (
                <div className="rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/[0.05] p-3" data-testid="reference-result">
                  <div className="flex items-center gap-2 text-sm font-bold text-[#00F0FF]"><CheckCircle2 size={16} /> Animation detected · {refResult.note}</div>
                  {refResult.multiple_text_elements && (
                    <div className="mt-2" data-testid="reference-elements">
                      <div className="flex items-start gap-1.5 text-[11px] text-[#f0b45a]" data-testid="reference-multi-note"><AlertTriangle size={12} className="mt-0.5" /> Multiple text elements detected — pick which one to import:</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {refResult.elements.map((el, i) => (
                          <button key={el.id} type="button" data-testid={`reference-element-${el.id}`}
                            onClick={() => { setRefElement(i); setCfg((c) => ({ ...c, ...el.preset.config })); }}
                            className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold capitalize ${refElement === i ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-[#A1A1AA] hover:border-white/30"}`}>
                            {el.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {(() => {
                    const sel = (refResult.elements && refResult.elements[refElement]) || { detected: refResult.detected, confidence: refResult.confidence };
                    return (
                      <>
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                          {Object.entries(sel.detected).map(([k, v]) => (
                            <div key={k} className={`flex items-center gap-1.5 capitalize ${v ? "text-white/80" : "text-[#555]"}`}>
                              {v ? <CheckCircle2 size={11} className="text-[#00F0FF]" /> : <X size={11} />} {k}
                            </div>
                          ))}
                        </div>
                        {Object.entries(sel.confidence).some(([, v]) => v === "estimated") && (
                          <div className="mt-2 text-[11px] text-[#f0b45a]" data-testid="reference-confidence-note">
                            <AlertTriangle size={11} className="mr-1 inline" /> Some properties were estimated ({Object.entries(sel.confidence).filter(([, v]) => v === "estimated").map(([k]) => k).join(", ")}) — edit the controls below to refine.
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {(refResult.unsupported_effects || []).length > 0 && (
                    <div className="mt-1 text-[11px] text-red-300" data-testid="reference-unsupported">✕ Not recreated: {refResult.unsupported_effects.join(", ")}</div>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-[11px] font-semibold text-white" data-testid="reference-compare-toggle">
                      <Eye size={13} /> <Switch checked={compare} onCheckedChange={setCompare} /> Compare reference
                    </label>
                    <button type="button" data-testid="reference-reanalyze-btn" onClick={analyzeRef} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:border-white/30"><RefreshCw size={12} /> Re-analyze</button>
                    <span className="text-[11px] text-[#666]">Edit the controls below, then Apply &amp; Preview. Save as a Brand preset to reuse.</span>
                  </div>
                </div>
              )}
            </Section>

            <Section icon={Wand2} title="Describe your text animation">
              <div className="rounded-xl border border-[#8B5CF6]/40 bg-[#8B5CF6]/[0.06] p-1 glow-accent">
                <textarea data-testid="prompt-textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                  placeholder={SAMPLE_PROMPT} rows={5}
                  className="w-full resize-none rounded-lg bg-transparent p-3 text-sm outline-none placeholder:text-[#555]" />
                <div className="flex items-center justify-between px-2 pb-1">
                  <button onClick={() => setPrompt(SAMPLE_PROMPT)} className="text-xs text-[#A1A1AA] hover:text-white">Use sample</button>
                  <button data-testid="interpret-btn" onClick={interpret} disabled={interpreting}
                    className="inline-flex items-center gap-2 rounded-full bg-[#8B5CF6] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#7c4ddb] disabled:opacity-60">
                    {interpreting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Interpret with AI
                  </button>
                </div>
              </div>
            </Section>

            <Section icon={Sparkles} title="Text animation presets">
              <div className="space-y-4" data-testid="text-anim-presets">
                {favs.length > 0 && (
                  <div key="__favs" data-testid="preset-favourites">
                    <div className="mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#FFD400]"><Star size={11} fill="#FFD400" /> Favourites</div>
                    <div className="grid grid-cols-3 gap-2">
                      {ALL_PRESETS.filter((p) => favs.includes(p.id)).map((p) => renderPresetCard(p))}
                    </div>
                  </div>
                )}
                {Object.entries(PRESET_GROUPS).map(([cat, list]) => (
                  list.length === 0 ? null : (
                    <div key={cat}>
                      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-[#71717a]">{cat}</div>
                      <div className="grid grid-cols-3 gap-2">
                        {list.map((p) => renderPresetCard(p))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            </Section>

            <Section icon={Sparkles} title="Look effect (moving background / light effects)">
              <CaptionFxControl fx={cfg.fx} onChange={(fx) => setCfg((c) => ({ ...c, fx }))} testid="anim-fx" />
              <p className="mt-1.5 text-[11px] text-[#666]">Golden particles, film grain, spectrum bars, petals, letterbox, video inside letters… Applies to captions that don't have their own effect.</p>
            </Section>
            <Section icon={Palette} title="Gradient text">
              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <span className="text-sm text-[#d4d4d8]">Fill text with a gradient</span>
                <Switch data-testid="gradient-text-toggle" checked={!!cfg.fillGradientEnabled} onCheckedChange={(v) => set("fillGradientEnabled", v)} />
              </div>
              {cfg.fillGradientEnabled && (
                <div className="mt-2 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <ColorField label="From" value={cfg.fillGradientFrom || "#00F0FF"} onChange={(v) => set("fillGradientFrom", v)} testid="gradient-from" />
                    <ColorField label="To" value={cfg.fillGradientTo || "#8B5CF6"} onChange={(v) => set("fillGradientTo", v)} testid="gradient-to" />
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-[#A1A1AA]"><span>Angle</span><span>{Math.round(cfg.fillGradientAngle ?? 90)}°</span></div>
                    <Slider data-testid="gradient-angle" min={0} max={180} step={5} value={[cfg.fillGradientAngle ?? 90]} onValueChange={([v]) => set("fillGradientAngle", v)} />
                  </div>
                </div>
              )}
            </Section>

            <Section icon={Type} title="Two fonts">
              <div className="grid grid-cols-4 gap-2">
                {[{ l: "One font", v: "single" }, { l: "Alternate", v: "alternate" }, { l: "1st / rest", v: "first-second" }, { l: "Highlight", v: "highlight" }].map((m) => (
                  <button key={m.v} data-testid={`fontmode-${m.v}`} onClick={() => set("fontMode", m.v)}
                    className={`rounded-lg border py-2 text-[11px] font-semibold transition-colors ${(cfg.fontMode || "single") === m.v ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/25"}`}>{m.l}</button>
                ))}
              </div>
              {(cfg.fontMode && cfg.fontMode !== "single") && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <FontPicker value={cfg.fontFamilyB || "Georgia"} onChange={(fam) => set("fontFamilyB", fam)} testId="font-family-b" page="Editor" />
                  <button data-testid="font-b-italic" onClick={() => set("fontStyleB", cfg.fontStyleB === "italic" ? "normal" : "italic")}
                    className={`rounded-lg border py-2 text-sm font-semibold transition-colors ${cfg.fontStyleB === "italic" ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white italic" : "border-white/10 bg-white/[0.02] text-[#A1A1AA]"}`}>Font B Italic</button>
                </div>
              )}
            </Section>

            <Section icon={Play} title="Animation speed">
              <div className="mb-1 flex justify-between text-xs text-[#A1A1AA]"><span>Enter duration</span><span>{(cfg.animEnterDur ?? 0.16).toFixed(2)}s</span></div>
              <Slider data-testid="anim-speed" min={0.05} max={1.2} step={0.01} value={[cfg.animEnterDur ?? 0.16]} onValueChange={([v]) => set("animEnterDur", v)} />
            </Section>

            <Section icon={Sparkles} title="Legacy preset styles">
              <div className="grid grid-cols-3 gap-2">
                {PRESETS.map((p) => (
                  <button key={p.id} data-testid={`preset-${p.id}`}
                    onClick={() => setCfg((c) => ({ ...c, animation: p.id, animationPresetId: undefined, wordByWord: false, letterMode: "", animationTarget: p.target === "karaoke" ? "karaoke" : c.animationTarget === "karaoke" ? "word" : c.animationTarget }))}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${(cfg || {}).animation === p.id && !(cfg || {}).animationPresetId ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                    <div className="text-xs font-bold">{p.name}</div>
                    <div className="text-[10px] leading-tight text-[#A1A1AA]">{p.d}</div>
                  </button>
                ))}
              </div>
            </Section>

            <Section icon={Play} title="Animation target">
              <div className="grid grid-cols-4 gap-2">
                {TARGETS.map((t) => (
                  <button key={t.v} data-testid={`target-${t.v}`} onClick={() => set("animationTarget", t.v)}
                    className={`rounded-lg border py-2 text-xs font-semibold transition-colors ${cfg.animationTarget === t.v ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/25"}`}>{t.l}</button>
                ))}
              </div>
            </Section>

            <Section icon={Type} title="Font">
              <div className="grid grid-cols-2 gap-2">
                <FontPicker value={cfg.fontFamily} onChange={(fam) => set("fontFamily", fam)} testId="font-family" page="Editor" />
                <select data-testid="font-weight" value={cfg.fontWeight} onChange={(e) => set("fontWeight", parseInt(e.target.value))} className="rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2 text-sm outline-none">
                  {WEIGHTS.map((w) => <option key={w.v} value={w.v}>{w.l}</option>)}
                </select>
              </div>
              <div className="mt-2">
                <input ref={fontFileRef} type="file" accept=".ttf,.otf,.woff" className="hidden" onChange={onUploadFont} data-testid="anim-font-upload-input" />
                <button type="button" onClick={() => fontFileRef.current?.click()} disabled={fontBusy} data-testid="anim-font-upload-btn"
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] py-2 text-xs font-semibold text-white transition-colors hover:border-[#00F0FF]/50 disabled:opacity-60">
                  {fontBusy ? <Loader2 size={13} className="animate-spin" /> : <Type size={13} />} Upload custom font (TTF / OTF / WOFF)
                </button>
              </div>
              <div className="mt-3 space-y-3">
                <div>
                  <div className="mb-1.5 flex justify-between text-[11px] text-[#A1A1AA]"><span>Font size</span><span className="tabular-nums text-white">{cfg.fontSize}</span></div>
                  <Slider data-testid="font-size" value={[cfg.fontSize]} min={40} max={160} step={2} onValueChange={([v]) => set("fontSize", v)} />
                </div>
                <div>
                  <div className="mb-1.5 flex justify-between text-[11px] text-[#A1A1AA]"><span>Line height</span><span className="tabular-nums text-white">{cfg.lineHeight}</span></div>
                  <Slider value={[cfg.lineHeight]} min={0.9} max={2} step={0.05} onValueChange={([v]) => set("lineHeight", v)} />
                </div>
              </div>
            </Section>

            <Section icon={Palette} title="Colours">
              <div className="mb-3 flex flex-wrap gap-2">
                {COLOR_PRESETS.map((c) => (
                  <button key={c.l} onClick={() => setCfg((p) => ({ ...p, textColor: c.t, highlightColor: c.h }))}
                    className="flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-1 text-[11px] transition-colors hover:border-white/30">
                    <span className="h-3 w-3 rounded-full" style={{ background: c.t }} /><span className="h-3 w-3 rounded-full" style={{ background: c.h }} />{c.l}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorField label="Primary text" value={cfg.textColor} onChange={(v) => set("textColor", v)} testid="color-text" />
                <ColorField label="Highlight" value={cfg.highlightColor} onChange={(v) => set("highlightColor", v)} testid="color-highlight" />
                <ColorField label="Outline" value={cfg.strokeColor ?? cfg.outlineColor} onChange={(v) => set("outlineColor", v)} testid="color-outline" />
                <ColorField label="Shadow" value={cfg.shadowColor} onChange={(v) => set("shadowColor", v)} testid="color-shadow" />
              </div>
              <div className="mt-3 flex items-center gap-5">
                <label className="flex items-center gap-2 text-sm"><Switch data-testid="toggle-outline" checked={cfg.strokeEnabled ?? cfg.outline} onCheckedChange={(v) => set("outline", v)} /> Outline</label>
                <label className="flex items-center gap-2 text-sm"><Switch data-testid="toggle-shadow" checked={cfg.shadowEnabled ?? cfg.shadow} onCheckedChange={(v) => set("shadow", v)} /> Shadow</label>
              </div>
            </Section>

            <Section icon={Layers} title="Text effects & background (default style)">
              <p className="mb-3 text-[11px] text-[#666]">These set the default look for every word. Override any single word from the transcript panel.</p>
              <StyleEditor value={baseStyle(cfg, false)} onPatch={(patch) => setCfg((c) => applyGlobalStyle(c, patch))} showFont={false} prefix="global-style" />
            </Section>

            <Section icon={MoveVertical} title="Position & alignment">
              <div className="grid grid-cols-5 gap-2">
                {POSITIONS.map((p) => (
                  <button key={p.v} data-testid={`position-${p.v}`} onClick={() => set("position", p.v)}
                    className={`rounded-lg border py-2 text-[11px] font-semibold transition-colors ${cfg.position === p.v ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white" : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/25"}`}>{p.l}</button>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {["left", "center", "right"].map((a) => (
                  <button key={a} onClick={() => set("align", a)} className={`rounded-lg border py-2 text-xs font-semibold capitalize transition-colors ${cfg.align === a ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/25"}`}>{a}</button>
                ))}
              </div>
            </Section>

            <Section icon={Smile} title="Emoji pops (timestamp-triggered)">
              <div className="mb-2 flex flex-wrap gap-1.5">
                {EMOJI_PALETTE.map((e) => (
                  <button key={e} data-testid={`emoji-${e}`} onClick={() => setEmoji(e)}
                    className={`rounded-lg border px-2 py-1 text-lg transition-colors ${emoji === e ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 hover:border-white/25"}`}>{e}</button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <select data-testid="emoji-word-select" value={emojiWord} onChange={(ev) => setEmojiWord(ev.target.value)}
                  className="flex-1 rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2 text-sm outline-none">
                  <option value="">Attach {emoji} to a word…</option>
                  {flatWords.map((w) => <option key={w.word_id} value={w.word_id}>{w.word} · {w.start.toFixed(2)}s</option>)}
                </select>
                <button data-testid="add-emoji-btn" onClick={addEmoji} className="flex items-center gap-1 rounded-lg bg-[#8B5CF6] px-3 py-2 text-sm font-bold text-white hover:bg-[#7c4ddb]"><Plus size={15} /> Add</button>
              </div>
              {(cfg.emojiTriggers || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="emoji-triggers">
                  {(cfg.emojiTriggers || []).map((tr) => (
                    <span key={tr.id || tr.word_id} className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2 py-1 text-xs">
                      {tr.emoji} {wordText(tr.word_id)}
                      <button onClick={() => removeEmoji(tr.id || tr.word_id)}><X size={12} className="text-[#A1A1AA] hover:text-white" /></button>
                    </span>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Highlighter} title="Auto highlight (AI)">
              <div className="flex items-center gap-2">
                <button data-testid="auto-highlight-btn" onClick={autoHighlight} disabled={highlighting}
                  className="flex items-center gap-2 rounded-lg bg-[#8B5CF6] px-3 py-2 text-sm font-bold text-white hover:bg-[#7c4ddb] disabled:opacity-60">
                  {highlighting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Highlight punchy words
                </button>
                <select data-testid="highlight-word-select" value={hiWord} onChange={(e) => setHiWord(e.target.value)}
                  className="flex-1 rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2 text-sm outline-none">
                  <option value="">Add a word…</option>
                  {flatWords.map((w) => <option key={w.word_id} value={w.word_id}>{w.word}</option>)}
                </select>
                <button data-testid="add-highlight-btn" onClick={addHi} className="rounded-lg border border-white/10 px-3 py-2 text-sm font-semibold hover:border-white/25"><Plus size={15} /></button>
              </div>
              <p className="mt-1.5 text-[11px] text-[#666]">Uses word timestamps; never edits your transcript.</p>
              {(cfg.highlightWordIds || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="highlight-chips">
                  {(cfg.highlightWordIds || []).map((wid) => (
                    <span key={wid} className="flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold" style={{ background: (cfg.highlightColor || "#FFD400") + "33", color: cfg.highlightColor }}>
                      {wordText(wid)}
                      <button onClick={() => removeHi(wid)}><X size={12} className="opacity-70 hover:opacity-100" /></button>
                    </span>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Bookmark} title="Brand presets">
              <div className="flex items-center gap-2">
                <input data-testid="brand-name-input" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Name this style (font, colours, animation…)"
                  className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
                <button data-testid="save-brand-btn" onClick={saveBrand} className="rounded-lg bg-white px-3 py-2 text-sm font-bold text-black hover:bg-white/90">Save</button>
              </div>
              {presets.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2" data-testid="brand-presets">
                  {presets.map((p) => (
                    <div key={p.id} className="group relative overflow-hidden rounded-lg border border-white/10 bg-white/[0.02] transition-colors hover:border-[#00F0FF]/50">
                      <button data-testid={`apply-brand-${p.id}`} onClick={() => applyPreset(p)} className="block w-full">
                        <div className="aspect-[9/16] w-full overflow-hidden checker">
                          <BrandThumbnail config={{ ...config, ...p.config }} />
                        </div>
                        <div className="truncate px-1.5 py-1 text-left text-[11px] font-semibold">{p.name}</div>
                      </button>
                      <button onClick={() => delPreset(p.id)} className="absolute right-1 top-1 rounded bg-black/60 p-1 opacity-0 transition-opacity group-hover:opacity-100"><Trash2 size={11} className="text-red-300" /></button>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {/* Right: live preview */}
          <div data-ws-pin="" className="flex flex-col border-l border-white/10 bg-[#0A0A0A] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Live Preview</span>
              <button onClick={() => onOpenChange(false)}><X size={16} className="text-[#A1A1AA] hover:text-white" /></button>
            </div>
            <div className="flex flex-1 items-center justify-center">
              <div className="checker relative overflow-hidden rounded-lg border border-white/10" style={{ aspectRatio: 9 / 16, height: "min(52vh, 460px)" }}>
                <canvas ref={canvasRef} width={360} height={640} className="h-full w-full" data-testid="modal-preview-canvas" />
                {compare && refUrl && (
                  <video src={refUrl} autoPlay loop muted playsInline data-testid="reference-compare-video"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                    style={{ opacity: refOpacity / 100 }} />
                )}
              </div>
            </div>
            {compare && refUrl && (
              <div className="mt-3" data-testid="reference-opacity-control">
                <div className="mb-1 flex justify-between text-[11px] text-[#A1A1AA]"><span>Reference opacity</span><span className="tabular-nums text-white">{refOpacity}%</span></div>
                <Slider value={[refOpacity]} min={0} max={100} step={5} onValueChange={([v]) => setRefOpacity(v)} />
              </div>
            )}
            <div className="mt-4 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/[0.05] p-2.5" data-testid="anim-save-preset">
              <div className="mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#a78bfa]"><Bookmark size={11} /> Save this preset</div>
              <div className="flex items-center gap-1.5">
                <input data-testid="anim-save-preset-name" value={saveName} onChange={(e) => setSaveName(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#8B5CF6]/60" />
                <button data-testid="anim-save-preset-btn" onClick={saveAsTextPreset} disabled={savingPreset}
                  className="flex items-center gap-1 rounded-lg bg-[#8B5CF6] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#7c4ddb] disabled:opacity-60">
                  {savingPreset ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Save
                </button>
              </div>
              <p className="mt-1 text-[10px] text-[#666]">Goes to My Text Presets — newest first.</p>
            </div>
            <div className="mt-3 space-y-2">
              <button data-testid="apply-animation-btn" onClick={apply} className="w-full rounded-full bg-[#00F0FF] py-3 text-sm font-bold text-black transition-colors hover:bg-[#00d4e0]">
                Apply &amp; Preview
              </button>
              <p className="text-center text-[11px] text-[#666]">Transparent background · timing from your transcript</p>
            </div>
          </div>
        </div>
      </DialogContent>
      {onApplySavedDesign && <SavedDesignsDialog open={savedOpen} onOpenChange={setSavedOpen}
        onPick={(p) => { if (onApplySavedDesign(p) !== false) { setSavedOpen(false); onOpenChange(false); } }} />}
    </Dialog>
  );
}
